import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { loadClubAttend, loadTodayRank, loadWeeklyRank, loadMonthlyRank } from '../../../lib/api/runningtrackMonitor';
import {
  Card, CardHead, CardTitle, CardBody, Tools, Hint, Kpis, Kpi, TableWrap, Tbl, Empty, Chip, Bar, Columns,
  ModalHead, ModalBody, Who,
} from './ZetaUi';
import ZetaRangePicker from './ZetaRangePicker';
import ZetaMemberModal from './ZetaMemberModal';
import {
  presetRange, rangeUnix, eachDay, dayKey, fromUnix, md, clock, km, num, maskPhone, dayStart,
} from './zetaUtil';

// 일별 기록 현황 — 프론트만으로 되는 범위.
//   출석 = 출입문 기록(관리자 출석 화면과 같은 /api/attend). 날짜별로 중복 없이 센다.
//   제타트랙 기록 인원 = 랭킹 API 의 참여 인원(total) — 오늘·이번 주·이번 달 합계만 있다(날짜별 값은 서버 수정 후).

const ZetaDailyStats = ({ clubID, socketKey }) => {
  const [range, setRange] = useState(presetRange('7'));
  const [attend, setAttend] = useState(null);       // 배열 | false(오류)
  const [totals, setTotals] = useState(null);       // { today, week, month, todayInfos }
  const [dayOpen, setDayOpen] = useState(null);     // Date
  const [memberID, setMemberID] = useState(null);

  useEffect(() => {
    if (!clubID) return;
    let alive = true;
    setAttend(null);
    const { startTime, endTime } = rangeUnix(range);
    loadClubAttend(clubID, startTime, endTime, socketKey)
      .then((res) => alive && setAttend(Array.isArray(res.data) ? res.data : false))
      .catch(() => alive && setAttend(false));
    return () => { alive = false; };
  }, [clubID, socketKey, range]);

  useEffect(() => {
    if (!clubID) return;
    let alive = true;
    Promise.all([loadTodayRank(clubID), loadWeeklyRank(clubID), loadMonthlyRank(clubID)])
      .then(([t, w, m]) => {
        if (!alive) return;
        setTotals({
          today: t.data && t.data.total, week: w.data && w.data.distance && w.data.distance.total,
          month: m.data && m.data.distance && m.data.distance.total, todayInfos: (t.data && t.data.infos) || [],
        });
      })
      .catch(() => alive && setTotals({ today: null, week: null, month: null, todayInfos: [] }));
    return () => { alive = false; };
  }, [clubID]);

  // 날짜 → (회원번호 → {첫 출입, 이름, 전화})
  const byDay = useMemo(() => {
    const map = {};
    (attend || []).forEach((x) => {
      const k = dayKey(fromUnix(x.attendTime));
      if (!map[k]) map[k] = {};
      const prev = map[k][x.playerID];
      if (!prev || x.attendTime < prev.attendTime) map[k][x.playerID] = x;
    });
    return map;
  }, [attend]);

  const days = eachDay(range);
  const counts = days.map((d) => ({ d, n: Object.keys(byDay[dayKey(d)] || {}).length }));
  const uniq = new Set((attend || []).map((x) => x.playerID)).size;
  const avg = days.length ? counts.reduce((a, x) => a + x.n, 0) / days.length : 0;
  const maxN = Math.max(1, ...counts.map((x) => x.n));
  const todayKey = dayKey(dayStart(new Date()));
  const t = totals || {};

  return (
    <Card>
      <CardHead>
        <CardTitle><span role="img" aria-label="그래프">📈</span> 일별 기록 현황</CardTitle>
        <Tools><ZetaRangePicker range={range} onChange={setRange} idPrefix="zeta-stats" /></Tools>
      </CardHead>
      <CardBody>
        <Kpis>
          <Kpi label="출석 인원 (중복 제외)" value={attend ? num(uniq) : '…'} unit="명" sub={`하루 평균 ${avg.toFixed(1)}명 · 출입문 기준`} />
          <Kpi label="오늘 제타트랙 기록 인원" value={t.today != null ? num(t.today) : '…'} unit="명" color="#1A73E9" sub="랭킹 참여 인원" />
          <Kpi label="이번 주 기록 인원" value={t.week != null ? num(t.week) : '…'} unit="명" color="#1A73E9" sub="월요일부터" />
          <Kpi label="이번 달 기록 인원" value={t.month != null ? num(t.month) : '…'} unit="명" color="#1A73E9" sub="1일부터" />
        </Kpis>
        <Hint>
          제타트랙 기록 인원은 오늘·이번 주·이번 달 합계입니다(랭킹 기준이라 이 클럽 회원의 다른 지점 기록도 포함).
          날짜를 누르면 그날 출석한 회원을 봅니다.
        </Hint>
        {attend === false ? <Empty>출석 기록을 불러오지 못했습니다 (관리자 로그인을 확인해 주세요)</Empty> : (
          <>
            <Columns
              items={counts.map((x) => ({ key: dayKey(x.d), label: `${x.d.getMonth() + 1}/${x.d.getDate()}`, value: x.n, title: `${md(x.d)} 출석 ${x.n}명 · 눌러서 회원 보기`, d: x.d }))}
              color="#adb5bd"
              onClick={(x) => setDayOpen(x.d)}
            />
            <TableWrap>
              <Tbl>
                <thead><tr><th>날짜</th><th className="r">출석 인원</th><th className="r">제타트랙 기록 인원</th><th /></tr></thead>
                <tbody>
                  {counts.slice().reverse().map((x) => (
                    <tr key={dayKey(x.d)} className="click" onClick={() => setDayOpen(x.d)}>
                      <td>{md(x.d)}</td>
                      <td className="r strong">{attend ? `${x.n}명` : '…'}<Bar style={{ width: Math.round((x.n / maxN) * 60) }} /></td>
                      <td className="r">{dayKey(x.d) === todayKey && t.today != null ? <><b>{t.today}명</b> <span style={{ color: '#868e96' }}>(오늘만)</span></> : <span style={{ color: '#adb5bd' }}>—</span>}</td>
                      <td className="muted">출석 회원 ›</td>
                    </tr>
                  ))}
                </tbody>
              </Tbl>
            </TableWrap>
          </>
        )}
      </CardBody>

      <Dialog open={!!dayOpen} onClose={() => setDayOpen(null)} maxWidth="md" fullWidth>
        {dayOpen ? (() => {
          const k = dayKey(dayOpen);
          const list = Object.values(byDay[k] || {}).sort((a, b) => a.attendTime - b.attendTime);
          const isToday = k === todayKey;
          const ran = {};
          if (isToday) (t.todayInfos || []).forEach((x) => { ran[x.memberID] = x; });
          return (
            <>
              <ModalHead>
                <div>
                  <h3>{md(dayOpen)} 출석한 회원</h3>
                  <div className="meta"><span>출석 {list.length}명</span>{isToday ? <span>· 오늘 제타트랙 기록 {Object.keys(ran).length}명</span> : null}</div>
                </div>
                <IconButton onClick={() => setDayOpen(null)} size="small" aria-label="닫기"><CloseIcon /></IconButton>
              </ModalHead>
              <ModalBody>
                <Hint>출입문 기록 기준입니다. 그날 누가 제타트랙을 했는지는 오늘만 표시됩니다(랭킹 명단). 회원을 누르면 상세가 열립니다.</Hint>
                <TableWrap>
                  <Tbl>
                    <thead><tr><th>이름</th><th>회원번호</th><th>전화</th><th>첫 출입</th><th>제타트랙</th></tr></thead>
                    <tbody>
                      {list.map((x) => (
                        <tr key={x.playerID} className="click" onClick={() => setMemberID(x.playerID)}>
                          <td><Who name={x.name} main={x.name} /></td>
                          <td className="muted">{x.playerID}</td>
                          <td className="muted">{maskPhone(x.phone)}</td>
                          <td>{clock(fromUnix(x.attendTime))}</td>
                          <td>{isToday ? (ran[x.playerID] ? <><Chip tone="blue">기록함</Chip> {km(ran[x.playerID].distance)}</> : <span style={{ color: '#868e96' }}>-</span>) : <span style={{ color: '#adb5bd' }}>—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Tbl>
                  {!list.length ? <Empty>이날 출석한 회원이 없습니다</Empty> : null}
                </TableWrap>
              </ModalBody>
            </>
          );
        })() : null}
      </Dialog>
      <ZetaMemberModal open={!!memberID} memberID={memberID} clubID={clubID} socketKey={socketKey} onClose={() => setMemberID(null)} />
    </Card>
  );
};

export default ZetaDailyStats;
